import { supabase } from '../lib/supabase';
import type { Copy, Edition, Loan, Read, Status } from '../book/types';
import { dayText, formatName, todayIn } from '../book/model';
import { notForMeWhy, readTypeKey, type ShelfBook, type ShelfKey } from './model';
import { checkDaysOf } from '../lib/libraryStates';
import { libraryOf, readListenOf, type FilterFacts, type FilterLists } from './filters';
import { surname, type RefBook } from './reference';
import { ownCover } from '../queue/lookups';

// Loads Shelves › Reading (Phase 6.2 Part 1), read with your own sign-in (the row rules show
// only your own rows and shared facts). Two steps:
//   1. every shelf's books, small rows only (title, place, read type), so counts, order and
//      "By read type" are exact, even for Future reads' 2,000-plus books;
//   2. the details for the books actually showing (author, cover, genre, series, copies, and on
//      cards the read, loan and note), in chunks, as Show next / Show all reveal more.

type Row = Record<string, unknown>;
const one = <T>(x: unknown): T | null => (Array.isArray(x) ? ((x[0] as T) ?? null) : ((x as T) ?? null));
const PAGE = 1000; // the database answers at most 1,000 rows at a time
const CHUNK = 120; // ids per detail query (keeps addresses short)

type Query = { range: (a: number, b: number) => PromiseLike<{ data: unknown; error: { message: string } | null }> };

export async function rows(q: PromiseLike<{ data: unknown; error: { message: string } | null }>): Promise<Row[]> {
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data as Row[] | null) ?? [];
}

/** Every row of a query, 1,000 at a time. */
export async function allRows(make: () => Query): Promise<Row[]> {
  const out: Row[] = [];
  for (let from = 0; ; from += PAGE) {
    const page = await rows(make().range(from, from + PAGE - 1));
    out.push(...page);
    if (page.length < PAGE) return out;
  }
}

const HEAD = 'id, label_number, status, status_changed_at, work_id, interest, added_at, added_how, import_batch_id, last_reviewed_at, not_from_library_at, not_for_me_kind, not_for_me_on, not_for_me_chips, read_again_at, try_again_at, favorite_at, work:works(title, goodreads_score, fiction), read_type:read_types(name)';

function head(b: Row, position: number | null): ShelfBook {
  const w = one<Row>(b.work);
  return {
    id: b.id as string,
    label: Number(b.label_number),
    workId: b.work_id as string,
    status: (b.status as Status) ?? null,
    title: (w?.title as string) ?? '',
    author: '',
    series: null,
    cover: null,
    readType: readTypeKey(one<Row>(b.read_type)?.name as string),
    readTypeName: (one<Row>(b.read_type)?.name as string) ?? null,
    literary: (w?.fiction as string) ?? null,
    favorite: Boolean(b.favorite_at),
    genre: null,
    subgenre: null,
    interest: b.interest === null || b.interest === undefined ? null : Number(b.interest),
    goodreads: w?.goodreads_score === null || w?.goodreads_score === undefined ? null : Number(w.goodreads_score),
    position,
    onShelfSince: (b.status_changed_at as string) ?? null,
    addedAt: (b.added_at as string) ?? '',
    addedHow: (b.added_how as ShelfBook['addedHow']) ?? undefined,
    importBatch: (b.import_batch_id as string) ?? null,
    lastReviewedAt: (b.last_reviewed_at as string) ?? null,
    notFromLibrary: Boolean(b.not_from_library_at),
    owned: undefined, // known once its details are read
    note: null,
    readAgain: Boolean(b.read_again_at),
    tryAgain: Boolean(b.try_again_at),
    readAgainAt: (b.read_again_at as string) ?? null,
    tryAgainAt: (b.try_again_at as string) ?? null,
    notForMe: b.status === 'not_for_me' ? { kind: (b.not_for_me_kind as 'abandoned' | 'passed_on') ?? null, on: (b.not_for_me_on as string) ?? null, chips: (b.not_for_me_chips as string[]) ?? [] } : undefined,
  };
}

export type Shelves = Record<ShelfKey, ShelfBook[]>;

/** Step 1: every shelf, small rows, in each shelf's order. */
export async function loadShelves(): Promise<Shelves> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const placed = (shelf: Status) => () =>
    db
      .from('book_positions')
      .select(`position, book:books!inner(${HEAD})`)
      .eq('shelf', shelf)
      .eq('book.status', shelf)
      .is('book.removed_at', null)
      .order('position') as unknown as Query;
  const byStatus = (status: Status | null, order: string, ascending = false) => () => {
    const q = db.from('books').select(HEAD).is('removed_at', null);
    return (status ? q.eq('status', status) : q.is('status', null)).order(order, { ascending, nullsFirst: false }).order('label_number', { ascending: false }) as unknown as Query;
  };
  const [reading, readingPlaces, upNext, future, finishedReads, notForMe, noStatus] = await Promise.all([
    allRows(byStatus('currently_reading', 'status_changed_at')),
    rows(db.from('book_positions').select('book_id, position').eq('shelf', 'currently_reading')),
    allRows(placed('up_next')),
    allRows(placed('future_reads')),
    allRows(
      () =>
        db
          .from('reads')
          .select(`book_id, ended_on, ended_precision, read_private(ended_words), book:books!inner(${HEAD})`)
          .eq('outcome', 'finished')
          .is('book.removed_at', null)
          .order('ended_on', { ascending: false, nullsFirst: false }) as unknown as Query,
    ),
    allRows(byStatus('not_for_me', 'not_for_me_on')),
    allRows(byStatus(null, 'added_at')),
  ]);
  const crPlace = new Map(readingPlaces.map((p) => [p.book_id as string, Number(p.position)]));
  // Finished: each book once, its latest finished read (already newest first).
  const seen = new Set<string>();
  const finished: ShelfBook[] = [];
  for (const r of finishedReads) {
    const b = one<Row>(r.book);
    if (!b || seen.has(b.id as string)) continue;
    seen.add(b.id as string);
    finished.push({
      ...head(b, null),
      finished: { on: (r.ended_on as string) ?? null, precision: (r.ended_precision as string) ?? null, words: (one<Row>(r.read_private)?.ended_words as string) ?? null },
    });
  }
  return {
    currently_reading: reading.map((b) => head(b, crPlace.get(b.id as string) ?? null)),
    up_next: upNext.map((p) => head(one<Row>(p.book)!, Number(p.position))),
    future_reads: future.map((p) => head(one<Row>(p.book)!, Number(p.position))),
    finished,
    not_for_me: notForMe.map((b) => head(b, null)),
    no_status: noStatus.map((b) => head(b, null)),
  };
}

/**
 * Shelves › Reference (Part 4): every Reference book in one read (1,000 at a time), with what
 * its cover, list row and grouping need: title, authors, a cover, read type. Genres and
 * subgenres come with the filter facts. `heads` feed the filters as the Reading tab's do.
 */
export async function loadReference(): Promise<{ heads: ShelfBook[]; books: RefBook[] }> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const sel = HEAD.replace('work:works(title, goodreads_score, fiction)', 'work:works(title, goodreads_score, fiction, work_people(role, sort_order, person:people(name)), editions(kind, cover_url))');
  const [list, covers] = await Promise.all([
    allRows(() => db.from('books').select(sel).eq('status', 'reference').is('removed_at', null).order('label_number') as unknown as Query),
    allRows(() => db.from('links').select('id, book_id, url').eq('label', 'Cover').is('copy_id', null).order('id') as unknown as Query),
  ]);
  const own = new Map(covers.map((l) => [l.book_id as string, l.url as string]));
  const heads: ShelfBook[] = [];
  const books: RefBook[] = [];
  for (const r of list) {
    const h = head(r, null);
    const w = one<Row>(r.work);
    const names = ((w?.work_people as Row[]) ?? [])
      .filter((p) => p.role === 'author' || p.role === 'co_author')
      .sort((x, y) => Number(x.sort_order ?? 0) - Number(y.sort_order ?? 0))
      .map((p) => (one<Row>(p.person)?.name as string) ?? '')
      .filter(Boolean);
    const eds = ((w?.editions as Row[]) ?? []).filter((e) => e.cover_url);
    const cover = own.get(h.id) ?? ((eds.find((e) => e.kind !== 'audiobook') ?? eds[0])?.cover_url as string) ?? null;
    heads.push({ ...h, author: names[0] ?? '', cover });
    books.push({
      id: h.id,
      label: h.label,
      title: h.title,
      author: names.length ? names[0] + (names.length > 1 ? ' and others' : '') : '',
      authorSort: names[0] ? surname(names[0]) : '',
      cover,
      readType: h.readType,
      genres: [],
      subgenres: [],
      addedAt: h.addedAt,
    });
  }
  return { heads, books };
}

/** What step 2 adds to a book. */
export type Details = Pick<ShelfBook, 'author' | 'series' | 'cover' | 'genre' | 'subgenre' | 'owned' | 'note' | 'reading' | 'loan' | 'loanCopy' | 'openIn' | 'lastFinished' | 'tries'>;

const FORMAT_NAMES: Record<string, string> = {
  kindle: 'Kindle', audible: 'Audible', paper: 'Paper', apple_books: 'Apple Books', google_play: 'Google Play', kobo: 'Kobo',
  libro_fm: 'Libro.fm', pdf_epub: 'PDF / EPUB', article: 'Article', libby: 'Libby', hoopla: 'Hoopla',
};

function chunks<T>(list: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += CHUNK) out.push(list.slice(i, i + CHUNK));
  return out;
}

/**
 * Step 2: details for these books. `cards` are the ones shown as cards, which also get their
 * open read (started, %, paused, copies used), library loan and latest note or why read.
 */
export async function loadDetails(books: ShelfBook[], cards: Set<string>): Promise<Map<string, Details>> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const out = new Map<string, Details>();
  const today = todayIn();
  for (const part of chunks(books)) {
    const ids = part.map((b) => b.id);
    const works = [...new Set(part.map((b) => b.workId))];
    const cardIds = ids.filter((id) => cards.has(id));
    const none = Promise.resolve([] as Row[]);
    const [people, series, editions, coverLinks, genres, subgenres, copies, reads, notes, priv] = await Promise.all([
      rows(db.from('work_people').select('work_id, role, sort_order, person:people(name)').in('work_id', works)),
      rows(db.from('series_works').select('work_id, label, position, series:series(name)').in('work_id', works)),
      rows(db.from('editions').select('work_id, kind, cover_url').in('work_id', works).not('cover_url', 'is', null)),
      rows(db.from('links').select('book_id, url').in('book_id', ids).is('copy_id', null).eq('label', 'Cover')),
      rows(db.from('book_genres').select('book_id, sort_order, genre:genres(name, short_name)').in('book_id', ids)),
      rows(db.from('book_subgenres').select('book_id, sort_order, subgenre:subgenres(name, short_name)').in('book_id', ids)),
      rows(db.from('copies').select('id, book_id, edition_id, format, how, state, acquired_on').in('book_id', ids)),
      cardIds.length
        ? rows(db.from('reads').select('id, book_id, outcome, started_on, started_at_percent, ended_on, ended_precision, stopped_percent, created_at, read_copies(copy_id, added_on, stopped_using_on), pauses(paused_on, resumed_on, check_in_after), progress_updates(noted_at, percent)').in('book_id', cardIds))
        : none,
      cardIds.length ? rows(db.from('reading_notes').select('book_id, noted_on, body, created_at').in('book_id', cardIds).order('noted_on', { ascending: false }).order('created_at', { ascending: false })) : none,
      cardIds.length ? rows(db.from('book_private').select('book_id, why_read, not_for_me_reason').in('book_id', cardIds)) : none,
    ]);
    // Not for me cards: why, in your words (Kristi, Oct 28): a pass's reason, or the stopped read's.
    const nfmIds = part.filter((b) => b.status === 'not_for_me' && cards.has(b.id)).map((b) => b.id);
    const stops = nfmIds.length
      ? await rows(db.from('reads').select('book_id, ended_on, stop_chips, read_private(stop_reason)').in('book_id', nfmIds).eq('outcome', 'stopped').order('ended_on', { ascending: false, nullsFirst: false }))
      : [];
    const copyIds = copies.filter((c) => cards.has(c.book_id as string) || c.how === 'library_loan').map((c) => c.id as string);
    const cardWorks = [...new Set(part.filter((b) => cards.has(b.id)).map((b) => b.workId))];
    const cardCopyIds = copies.filter((c) => cards.has(c.book_id as string)).map((c) => c.id as string);
    const [loans, cardEditions, copyLinks] = await Promise.all([
      copyIds.length
        ? rows(
            db
              .from('library_loans')
              .select('id, copy_id, step, hold_placed_on, expected_on, ready_on, borrow_by, borrowed_on, loan_days, due_on, returned_on, cancelled_on, place_in_line, library_copies, created_at, quiet_until, library:libraries(name, libby_code, check_days_before)')
              .in('copy_id', copyIds)
              .not('step', 'in', '(returned,cancelled)'),
          )
        : none,
      // For Open in on cards (Kristi, Oct 28: every copy you can read now): the IDs and links the book page uses.
      cardWorks.length ? rows(db.from('editions').select('id, work_id, kind, asin, audible_id').in('work_id', cardWorks)) : none,
      cardCopyIds.length ? rows(db.from('links').select('copy_id, kind, url').in('copy_id', cardCopyIds)) : none,
    ]);
    for (const b of part) {
      const names = people
        .filter((p) => p.work_id === b.workId && (p.role === 'author' || p.role === 'co_author'))
        .sort((x, y) => Number(x.sort_order ?? 0) - Number(y.sort_order ?? 0))
        .map((p) => (one<Row>(p.person)?.name as string) ?? '')
        .filter(Boolean);
      const sw = series.filter((s) => s.work_id === b.workId).sort((x, y) => Number(x.position ?? 0) - Number(y.position ?? 0))[0];
      const sName = sw ? (one<Row>(sw.series)?.name as string) : null;
      const sLabel = sw ? ((sw.label as string) ?? (sw.position !== null && sw.position !== undefined ? `Book ${Number(sw.position)}` : null)) : null;
      const eds = editions.filter((e) => e.work_id === b.workId);
      const cover = ownCover(coverLinks.find((l) => l.book_id === b.id)?.url as string | undefined) ?? ((eds.find((e) => e.kind !== 'audiobook') ?? eds[0])?.cover_url as string) ?? null;
      const g = genres.filter((x) => x.book_id === b.id).sort((x, y) => Number(x.sort_order ?? 0) - Number(y.sort_order ?? 0))[0];
      const gr = g ? one<Row>(g.genre) : null;
      // The first subgenre you listed (Kristi, Oct 28: one, to help when scanning a shelf).
      const sg = subgenres.filter((x) => x.book_id === b.id).sort((x, y) => Number(x.sort_order ?? 0) - Number(y.sort_order ?? 0))[0];
      const sgr = sg ? one<Row>(sg.subgenre) : null;
      const mine = copies.filter((c) => c.book_id === b.id);
      const owned = mine.some((c) => c.how !== 'library_loan' && c.state === 'have_it');
      const myLoans = loans.filter((l) => mine.some((c) => c.id === l.copy_id));
      const order = ['borrowed', 'ready', 'on_hold'];
      // The most urgent loan: borrowed (soonest due, so one that ended comes first), ready, on hold.
      const loanRow = myLoans.sort(
        (x, y) => order.indexOf(x.step as string) - order.indexOf(y.step as string) || String(x.due_on ?? x.expected_on ?? '9999').localeCompare(String(y.due_on ?? y.expected_on ?? '9999')),
      )[0];
      // Two library copies with loans (The Redbreast: Kindle and Libby audio): the pill names which.
      const loanCopies = mine.filter((c) => c.how === 'library_loan' && myLoans.some((l) => l.copy_id === c.id));
      const loanCopyRow = loanRow && loanCopies.length > 1 ? mine.find((c) => c.id === loanRow.copy_id) : undefined;
      const loanCopy = loanCopyRow
        ? formatName(loanCopyRow as unknown as Copy, cardEditions.find((e) => e.id === loanCopyRow.edition_id) as unknown as Edition | undefined)
        : undefined;
      const asLoan = (l: Row): Loan => {
        const lib = one<Row>(l.library);
        return { ...(l as unknown as Loan), library: (lib?.name as string) ?? null, libby_code: (lib?.libby_code as string) ?? null, check_days: checkDaysOf(lib?.check_days_before), events: [] };
      };
      const loan = loanRow ? asLoan(loanRow) : undefined;
      const d: Details = {
        author: names.length ? names[0] + (names.length > 1 ? ' and others' : '') : '',
        series: sName ? [sName, sLabel].filter(Boolean).join(' · ') : null,
        cover,
        genre: gr ? ((gr.short_name as string) ?? (gr.name as string)) : null,
        subgenre: sgr ? ((sgr.short_name as string) ?? (sgr.name as string)) : null,
        owned,
        note: null,
        loan,
        loanCopy,
      };
      if (cards.has(b.id)) {
        const myReads = reads.filter((x) => x.book_id === b.id);
        const r = myReads.find((x) => x.outcome === 'open');
        // Open in: the shapes the book page's openTargets reads.
        d.openIn = {
          copies: mine.map(
            (c) =>
              ({
                id: c.id,
                edition_id: c.edition_id ?? null,
                format: c.format,
                how: c.how,
                state: c.state,
                acquired_on: c.acquired_on ?? null,
                library_kindle_url: (copyLinks.find((l) => l.copy_id === c.id && l.kind === 'library_kindle')?.url as string) ?? null,
                link: null,
                loans: loans.filter((l) => l.copy_id === c.id).map(asLoan),
              }) as unknown as Copy,
          ),
          editions: cardEditions.filter((e) => e.work_id === b.workId).map((e) => ({ id: e.id, kind: e.kind, asin: e.asin ?? null, audible_id: e.audible_id ?? null }) as unknown as Edition),
          reads: myReads.map(
            (x) =>
              ({
                id: x.id,
                outcome: x.outcome,
                started_on: x.started_on ?? null,
                created_at: x.created_at,
                copies: ((x.read_copies as Row[]) ?? []).map((rc) => ({ copy_id: rc.copy_id, added_on: rc.added_on ?? null, stopped_using_on: rc.stopped_using_on ?? null })),
                pauses: [],
                progress: [],
              }) as unknown as Read,
          ),
        };
        // Marks on cards (Kristi, Oct 28): "Finished Mar 2024" (the latest finish); ↺ tries since the last finish.
        const fin = myReads.filter((x) => x.outcome === 'finished').sort((x, y) => String(y.ended_on ?? '').localeCompare(String(x.ended_on ?? '')))[0];
        d.lastFinished = fin ? { on: (fin.ended_on as string) ?? null, precision: (fin.ended_precision as string) ?? null } : null;
        const since = fin?.ended_on ? String(fin.ended_on) : '';
        const stopped = myReads.filter((x) => x.outcome === 'stopped' && String(x.ended_on ?? x.created_at ?? '') >= since).sort((x, y) => String(y.ended_on ?? '').localeCompare(String(x.ended_on ?? '')));
        d.tries = stopped.length ? { count: stopped.length, lastStoppedAt: stopped[0]!.stopped_percent === null || stopped[0]!.stopped_percent === undefined ? null : Number(stopped[0]!.stopped_percent) } : null;
        if (r) {
          const pu = ((r.progress_updates as Row[]) ?? []).sort((x, y) => String(y.noted_at).localeCompare(String(x.noted_at)))[0];
          const pause = ((r.pauses as Row[]) ?? []).find((p) => !p.resumed_on);
          const using = ((r.read_copies as Row[]) ?? []).filter((rc) => !rc.stopped_using_on).map((rc) => rc.copy_id);
          const formats = [...new Set(mine.filter((c) => using.includes(c.id)).map((c) => FORMAT_NAMES[c.format as string] ?? String(c.format)))];
          d.reading = {
            startedOn: (r.started_on as string) ?? null,
            percent: pu ? Number(pu.percent) : r.started_at_percent ? Number(r.started_at_percent) : null,
            pausedOn: pause ? ((pause.paused_on as string) ?? null) : null,
            checkInAfter: pause ? ((pause.check_in_after as string) ?? null) : null,
            formats,
          };
        }
        const n = notes.find((x) => x.book_id === b.id);
        const p = priv.find((x) => x.book_id === b.id);
        const why = p?.why_read as string | undefined;
        // The latest reading note, date first; else Why I want to read it (the Currently Reading card).
        d.note = n ? `${n.noted_on ? dayText(n.noted_on as string, today) + ' · ' : ''}${n.body as string}` : (why ?? null);
        if (b.status === 'not_for_me') {
          const stop = b.notForMe?.kind === 'abandoned' ? stops.find((x) => x.book_id === b.id) : undefined;
          d.note = notForMeWhy(
            [...(b.notForMe?.chips ?? []), ...((stop?.stop_chips as string[]) ?? [])],
            (p?.not_for_me_reason as string) ?? (one<Row>(stop?.read_private)?.stop_reason as string) ?? null,
          );
        }
      }
      out.set(b.id, d);
    }
  }
  return out;
}

/**
 * What every book on the Reading tab offers the filters (Part 3), and the lists the groups are
 * built from: read once, a few requests of up to 1,000 rows, then filtering is instant.
 */
export async function loadFilterFacts(shelves: Shelves, today: string, reference: ShelfBook[] = []): Promise<{ facts: FilterFacts[]; lists: FilterLists }> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const byId = new Map<string, { b: ShelfBook; shelf: ShelfKey | 'reference' }>();
  for (const key of ['currently_reading', 'up_next', 'future_reads', 'not_for_me', 'no_status'] as ShelfKey[]) for (const b of shelves[key]) byId.set(b.id, { b, shelf: key });
  for (const b of shelves.finished) if (!byId.has(b.id)) byId.set(b.id, { b, shelf: 'finished' });
  // Reference books (Part 4) get facts too; one finished before stays on Finished as well.
  for (const b of reference) if (!byId.has(b.id)) byId.set(b.id, { b, shelf: 'reference' });
  const finished = new Set(shelves.finished.map((b) => b.id));
  const q = (t: string, cols: string) => () => db.from(t).select(cols).order('id') as unknown as Query;
  const [bg, bs, bt, copies, loans, genres, subgenres, tags, settings, defaults] = await Promise.all([
    allRows(q('book_genres', 'id, book_id, genre_id')),
    allRows(q('book_subgenres', 'id, book_id, subgenre_id')),
    allRows(q('book_tags', 'id, book_id, tag_id')),
    allRows(q('copies', 'id, book_id, format, how, state, edition:editions(asin, audible_id)')),
    rows(db.from('library_loans').select('copy_id, step, due_on, expected_on, quiet_until, library:libraries(check_days_before)').not('step', 'in', '(returned,cancelled)')),
    rows(db.from('genres').select('id, name, short_name, sort_order, hidden').order('sort_order')),
    rows(db.from('subgenres').select('id, name, short_name, genre_id, sort_order, hidden').order('sort_order')),
    rows(db.from('tags').select('id, name, sort_order').order('sort_order')),
    rows(db.from('settings').select('key, value').in('key', ['adding.recently_added_days', 'literary_genres.on'])),
    rows(db.from('setting_defaults').select('key, value').in('key', ['adding.recently_added_days', 'literary_genres.on'])),
  ]);
  const setting = (k: string) => settings.find((s) => s.key === k)?.value ?? defaults.find((s) => s.key === k)?.value;
  const days = Number(setting('adding.recently_added_days') ?? 30) || 30;
  const literaryOn = Object.entries((setting('literary_genres.on') as Record<string, boolean>) ?? { fiction: true, nonfiction: true, poetry: true })
    .filter(([, on]) => on)
    .map(([k]) => k);
  const group = (list: Row[], col: string) => {
    const m = new Map<string, string[]>();
    for (const r of list) m.set(r.book_id as string, [...(m.get(r.book_id as string) ?? []), r[col] as string]);
    return m;
  };
  const gOf = group(bg, 'genre_id');
  const sOf = group(bs, 'subgenre_id');
  const tOf = group(bt, 'tag_id');
  const activeLoan = new Set(loans.map((l) => l.copy_id as string));
  const loansOf = new Map<string, { step: string; due_on: string | null; expected_on: string | null; check_days: number; quiet_until: string | null }[]>();
  for (const l of loans)
    loansOf.set(l.copy_id as string, [
      ...(loansOf.get(l.copy_id as string) ?? []),
      // Check Libby's window is the library's own (R2 question 4, Kristi Nov 2).
      { step: l.step as string, due_on: (l.due_on as string) ?? null, expected_on: (l.expected_on as string) ?? null, check_days: checkDaysOf(one<Row>(l.library)?.check_days_before), quiet_until: (l.quiet_until as string) ?? null },
    ]);
  const cOf = new Map<string, Row[]>();
  for (const c of copies) cOf.set(c.book_id as string, [...(cOf.get(c.book_id as string) ?? []), c]);
  const facts: FilterFacts[] = [];
  for (const [id, { b, shelf }] of byId) {
    const mine = cOf.get(id) ?? [];
    const have = mine.filter((c) => (c.how !== 'library_loan' && c.state === 'have_it') || (c.how === 'library_loan' && activeLoan.has(c.id as string)));
    const own = mine.filter((c) => c.how !== 'library_loan' && c.state === 'have_it');
    const formats = [...new Set(have.map((c) => String(c.format)))];
    facts.push({
      id,
      shelf,
      finished: finished.has(id),
      readType: b.readType ?? (b.readTypeName ? 'other' : 'none'),
      readListen: readListenOf(formats),
      interest: b.interest,
      owned: own.length > 0,
      shared: own.some((c) => c.how === 'shared'),
      formats,
      literary: b.literary ?? null,
      genres: gOf.get(id) ?? [],
      subgenres: sOf.get(id) ?? [],
      tags: tOf.get(id) ?? [],
      library: libraryOf(mine.filter((c) => c.how === 'library_loan').flatMap((c) => loansOf.get(c.id as string) ?? []), today, shelf === 'currently_reading'),
      addedAt: b.addedAt,
      imported: b.addedHow === 'import',
      readAgain: Boolean(b.readAgain),
      tryAgain: Boolean(b.tryAgain),
      favorite: Boolean(b.favorite),
    });
  }
  return {
    facts,
    lists: {
      readTypes: [],
      genres: genres.map((g) => ({ id: g.id as string, name: g.name as string, short: (g.short_name as string) ?? null, hidden: Boolean(g.hidden) })),
      subgenres: subgenres.filter((s) => !s.hidden).map((s) => ({ id: s.id as string, name: ((s.short_name as string) ?? (s.name as string)) as string, genreId: s.genre_id as string })),
      tags: tags.map((t) => ({ id: t.id as string, name: t.name as string })),
      literaryOn,
      recentDays: days,
    },
  };
}

/** Reference books as small rows (no covers or authors), for lists that span every shelf (the Queue's Recently added). */
export async function loadReferenceHeads(): Promise<ShelfBook[]> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const list = await allRows(() => db.from('books').select(HEAD).eq('status', 'reference').is('removed_at', null).order('label_number') as unknown as Query);
  return list.map((b) => head(b, null));
}


/**
 * Just the covers, for table rows that are books (check-up, Kristi Nov 2: every book row shows its
 * cover). The same choice as the shelves: your own cover link, else an edition that isn't an
 * audiobook, else any edition's.
 */
export async function loadCovers(books: { id: string; workId: string }[]): Promise<Map<string, string | null>> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const out = new Map<string, string | null>();
  for (const part of chunks(books)) {
    // Rows without a work ask nothing of editions (an empty "in ()" is a wasted request).
    const works = [...new Set(part.map((b) => b.workId).filter(Boolean))];
    const [editions, links] = await Promise.all([
      works.length === 0 ? Promise.resolve([] as Row[]) : rows(db.from('editions').select('work_id, kind, cover_url').in('work_id', works).not('cover_url', 'is', null)),
      rows(db.from('links').select('book_id, url').in('book_id', part.map((b) => b.id)).is('copy_id', null).eq('label', 'Cover')),
    ]);
    for (const b of part) {
      const eds = editions.filter((e) => e.work_id === b.workId);
      out.set(b.id, ownCover(links.find((l) => l.book_id === b.id)?.url as string | undefined) ?? ((eds.find((e) => e.kind !== 'audiobook') ?? eds[0])?.cover_url as string) ?? null);
    }
  }
  return out;
}
